/**
 * Recipe books: the org's reference shelf for the development chat. See
 * docs/architecture.md D11.
 *
 * A book is a PDF (digital or scanned) or a plain-text / Markdown file. It is
 * uploaded to the `Library` bucket under the org's own prefix, with a
 * `.metadata.json` sidecar naming the org and book, and a Bedrock Managed
 * Knowledge Base parses, chunks and embeds it. The chat searches it through
 * a tool, filtered to the caller's org.
 *
 * Everything here is pure: what may be uploaded, where it lives, what the
 * sidecar says, what the shelf costs to keep, and how retrieved passages are
 * shown to the model. The I/O is in packages/api/src/routes/books.ts and
 * packages/chat/src/books.ts.
 */
import { priced } from "./budget.js";
const MB = 1024 * 1024;
/**
 * What the shelf accepts. PDFs are capped at 200 MB — a photographed
 * cookbook runs large and the managed parser takes up to 500 MB, but storage
 * is billed on raw size ($5/GB-month), so the cap is also a cost guard. Text
 * is capped at 20 MB, which is several thousand recipes.
 */
const FORMATS = {
    "application/pdf": { format: "pdf", ext: "pdf", maxBytes: 200 * MB },
    "text/plain": { format: "text", ext: "txt", maxBytes: 20 * MB },
    "text/markdown": { format: "text", ext: "md", maxBytes: 20 * MB },
};
/** The content types the upload accepts, for the error message and the file picker. */
export const BOOK_CONTENT_TYPES = Object.keys(FORMATS);
/**
 * Browsers report `.md` inconsistently — `text/markdown`, `text/x-markdown`,
 * or nothing at all — so the filename settles it when the type is vague.
 */
export function bookContentType(contentType, filename) {
    const ct = contentType.split(";")[0].trim().toLowerCase();
    if (FORMATS[ct])
        return ct;
    const ext = filename.toLowerCase().split(".").pop() ?? "";
    if (ext === "md" || ext === "markdown")
        return "text/markdown";
    if (ext === "txt")
        return "text/plain";
    if (ext === "pdf")
        return "application/pdf";
    return null;
}
/** The upload request, checked. A 400 names the field. */
export function validateNewBook(v) {
    if (typeof v !== "object" || v === null)
        return { ok: false, error: "Body must be a JSON object" };
    const r = v;
    const filename = typeof r["filename"] === "string" ? r["filename"].trim() : "";
    if (!filename || filename.length > 200)
        return { ok: false, error: "filename is required (at most 200 characters)" };
    const contentType = bookContentType(typeof r["contentType"] === "string" ? r["contentType"] : "", filename);
    if (!contentType) {
        return { ok: false, error: "contentType must be a PDF, plain text (.txt) or Markdown (.md) file" };
    }
    const rule = FORMATS[contentType];
    const sizeBytes = r["sizeBytes"];
    if (typeof sizeBytes !== "number" || !Number.isInteger(sizeBytes) || sizeBytes <= 0) {
        return { ok: false, error: "sizeBytes must be a positive whole number" };
    }
    if (sizeBytes > rule.maxBytes) {
        return { ok: false, error: `sizeBytes: a ${rule.ext} file may be at most ${rule.maxBytes / MB} MB` };
    }
    const rawTitle = typeof r["title"] === "string" ? r["title"].trim() : "";
    const title = rawTitle || filename.replace(/\.[^.]+$/, "");
    if (title.length > 160)
        return { ok: false, error: "title must be at most 160 characters" };
    return { ok: true, value: { title, filename, contentType, format: rule.format, ext: rule.ext, sizeBytes } };
}
// ---------------------------------------------------------------- the bucket
/**
 * Where an org's books live in the Library bucket. The prefix is the
 * tenancy check on the way back, exactly as `slipPrefix` is for slips.
 */
export function bookPrefix(orgId) {
    return `books/${orgId}/`;
}
export function bookObjectKey(orgId, bookId, ext) {
    return `${bookPrefix(orgId)}${bookId}.${ext}`;
}
/** The knowledge base reads `<key>.metadata.json` beside each document. */
export function bookMetadataKey(objectKey) {
    return `${objectKey}.metadata.json`;
}
/** True only for a key inside this org's prefix with no path tricks. */
export function bookBelongsTo(key, orgId) {
    return key.startsWith(bookPrefix(orgId)) && !key.includes("..") && key.length < 300;
}
/**
 * The sidecar the knowledge base reads at ingestion. `orgId` is what the
 * chat filters on, so one org can never retrieve another's passages; `bookId`
 * lets the chat drop a book the moment it is deleted, before the re-sync that
 * removes its vectors has run. Neither is embedded — only the title is, so a
 * question naming the book finds it.
 */
export function bookMetadata(book) {
    const str = (value, includeForEmbedding) => ({
        value: { type: "STRING", stringValue: value },
        includeForEmbedding,
    });
    return {
        metadataAttributes: {
            orgId: str(book.orgId, false),
            bookId: str(book.bookId, false),
            title: str(book.title, true),
        },
    };
}
/** A book id: short, URL-safe, sortable enough to read in a key. */
export function newBookId(now = new Date(), rand = Math.random) {
    const day = now.toISOString().slice(0, 10).replace(/-/g, "");
    return `${day}-${rand().toString(36).slice(2, 10)}`;
}
// --------------------------------------------------------------------- cost
/**
 * Managed Knowledge Base list prices (US, checked 2026-10-02; Sydney may run
 * up to ~30% higher). Kept here so the Books screen and the chat's metering
 * read the same figures — move them together when AWS does.
 */
export const KB_STORAGE_CENTS_PER_GB_MONTH = 500;
export const KB_RETRIEVE_CENTS_PER_CALL = 0.1;
/** What the shelf costs to keep for a month, in cents, at the storage rate. */
export function shelfStorageCentsPerMonth(totalBytes) {
    return (totalBytes / (1024 * MB)) * KB_STORAGE_CENTS_PER_GB_MONTH;
}
/**
 * The bytes the shelf is billed for: every book whose file is in the bucket.
 * Not one on its way off, whose file is already gone, and not one still
 * uploading — an upload abandoned halfway leaves its row behind, and the
 * size on it is only what the browser said it was going to send.
 */
export function shelfBytes(books) {
    return books
        .filter((b) => b.status !== "deleting" && b.status !== "uploading")
        .reduce((sum, b) => sum + (b.sizeBytes || 0), 0);
}
/**
 * The shelf's storage for a month at the org's multiplier. A whole month for
 * whatever is on the shelf now, not prorated: predictable for the org, and
 * taking a book off frees its share at once.
 */
export function shelfStorage(books, multiplier) {
    const bytes = shelfBytes(books);
    return { bytes, ...priced(shelfStorageCentsPerMonth(bytes), multiplier) };
}
/** What `n` searches cost, in cents. */
export function retrievalCents(searches) {
    return searches * KB_RETRIEVE_CENTS_PER_CALL;
}
/** Page-number keys the knowledge base has been seen to use, newest first. */
const PAGE_KEYS = ["_page_number", "x-amz-bedrock-kb-document-page-number", "page_number", "page"];
function str(v) {
    return typeof v === "string" && v.trim() ? v.trim() : null;
}
/** A passage per chunk; chunks with no text are dropped. */
export function passagesFrom(results) {
    const out = [];
    for (const r of results) {
        const text = r.content?.text?.trim();
        if (!text)
            continue;
        const m = r.metadata ?? {};
        let page = null;
        for (const k of PAGE_KEYS) {
            const n = Number(m[k]);
            if (m[k] !== undefined && Number.isFinite(n) && n > 0) {
                page = Math.round(n);
                break;
            }
        }
        out.push({
            bookId: str(m["bookId"]),
            title: str(m["title"]) ?? "Untitled book",
            page,
            text,
            score: typeof r.score === "number" ? r.score : null,
        });
    }
    return out;
}
/** A passage's citation: `[The Joy of Mixology p.112]`, or `[Notes]` for text with no pages. */
export function citation(p) {
    return p.page ? `[${p.title} p.${p.page}]` : `[${p.title}]`;
}
/**
 * What the `search_recipe_books` tool hands the model. Each passage is
 * capped so one long chunk cannot crowd out the rest — and so the token
 * cost of a search stays near the ~2,500 the estimate in D11 assumes.
 */
export function formatPassages(passages, maxCharsEach = 2000) {
    if (passages.length === 0)
        return "No passages in the org's recipe books matched that search.";
    return passages
        .map((p, i) => {
        const body = p.text.length > maxCharsEach ? `${p.text.slice(0, maxCharsEach)}…` : p.text;
        return `${i + 1}. ${citation(p)}\n${body}`;
    })
        .join("\n\n");
}
/**
 * A job syncs the whole data source, not one book, so its own verdict says
 * only that it has finished. Whether a given book made it into the index is
 * `documentOutcome`'s question: a job can complete cleanly having scanned
 * nothing at all, which is how a misconfigured data-source filter looks.
 */
export function jobOutcome(job) {
    switch (job.status) {
        case "COMPLETE":
            return { done: true, ok: true };
        case "FAILED":
        case "STOPPED":
            return {
                done: true,
                ok: false,
                error: job.failureReasons?.length ? job.failureReasons.join("; ").slice(0, 500) : `Indexing ${job.status.toLowerCase()}`,
            };
        default:
            return { done: false };
    }
}
/** How the knowledge base names a book's file: its S3 URI. */
export function bookDocumentUri(bucket, objectKey) {
    return `s3://${bucket}/${objectKey}`;
}
const reason = (doc, fallback) => (doc.statusReason?.trim() || fallback).slice(0, 500);
/**
 * A book's own status in the index, asked once the job it waited on has
 * finished. `job` is that job's outcome, so a document the job never reached
 * can carry the job's own error rather than a generic one.
 *
 * Metadata failures count as failures, not warnings: the chat finds a book
 * only through its `orgId` and `bookId` metadata, so a book indexed without
 * them is unreachable however well its text was read.
 */
export function documentOutcome(doc, job) {
    switch (doc.status) {
        case "INDEXED":
            return { state: "ready" };
        case "PARTIALLY_INDEXED":
            return { state: "ready", warning: reason(doc, "Only part of this file could be read") };
        case "METADATA_PARTIALLY_INDEXED":
        case "METADATA_UPDATE_FAILED":
            return { state: "failed", error: reason(doc, "The book's details could not be attached, so the chat cannot find it") };
        case "FAILED":
            return { state: "failed", error: reason(doc, "The file could not be read") };
        case "IGNORED":
            return { state: "failed", error: reason(doc, "The sync skipped this file") };
        case "NOT_FOUND":
        case undefined:
            return job.done && !job.ok
                ? { state: "failed", error: job.error }
                : { state: "failed", error: "The sync did not pick this file up — the data source may be filtering it out" };
        default:
            // STARTING, PENDING, IN_PROGRESS, DELETING, DELETE_IN_PROGRESS: still moving.
            return { state: "waiting" };
    }
}
/**
 * The same question for a book being removed: it is gone once the index no
 * longer has it, and still going while the index is deleting it. Anything
 * else means the sync did not drop it, and the removal is tried again.
 */
export function removalOutcome(doc) {
    switch (doc.status) {
        case "NOT_FOUND":
        case undefined:
            return "gone";
        case "DELETING":
        case "DELETE_IN_PROGRESS":
            return "waiting";
        default:
            return "retry";
    }
}
/** The books the chat may cite: ready ones only. */
export function citableBookIds(books) {
    return books.filter((b) => b.status === "ready").map((b) => b.bookId);
}
/**
 * The Retrieve filter: this org's metadata AND one of its ready books. The
 * org clause is the tenancy guard; the book clause drops a book the moment
 * it is deleted or fails, without waiting for the re-sync.
 */
export function retrievalFilter(orgId, bookIds) {
    return {
        andAll: [
            { equals: { key: "orgId", value: orgId } },
            { in: { key: "bookId", value: [...bookIds] } },
        ],
    };
}
//# sourceMappingURL=books.js.map